import { axisBottom, axisTop, axisLeft, axisRight } from 'd3-axis';
import { select } from 'd3-selection';
import React, { useLayoutEffect, useRef } from 'react';
import { getExtent } from '../utils/scale';
import { Scale } from '../types/scale';

const CHAR_LENGTH = 8; // px
const LABEL_SIZE = 15; // px

export type Formatter = (value: number | { valueOf(): number }) => string;

type Position = 'bottom' | 'top' | 'left' | 'right';

interface Props {
  position?: Position;
  scale: Scale;
  interval: number;
  formatter: Formatter;
  label?: string;
  height?: number;
}

export const Axis: React.FC<Props> = ({
  position = 'bottom',
  scale,
  interval,
  formatter,
  label,
  height,
}) => {
  const duration = Math.abs(getExtent(scale.domain()));
  const width = Math.abs(getExtent(scale.range()));
  const groupRef = useRef<SVGGElement>(null);
  const tickCounts = Math.floor(duration / interval + 1);
  const { x, y, rotation } = getPosition(position, width, label, height);

  useLayoutEffect(() => {
    if (groupRef.current == null) {
      return;
    }

    const axis = getAxis(position);
    const axisFactory = axis(scale).ticks(tickCounts).tickFormat(formatter);
    const groupSelection = select(groupRef.current);

    axisFactory(groupSelection);
    groupSelection
      .selectAll<SVGTextElement, number>('text')
      .attr('dx', (timestamp, idx, arr) => {
        if (position !== 'top' && position !== 'bottom') {
          // we only apply changes to the horizontal scales
          return 0;
        }
        /**
         * timestamp: the timestamp of the tick, i.e. 1000, 2000, etc
         * text: the label of the tick, i.e. '1', '2', etc
         * xPosition: the x-position of the label
         */
        const text = arr[idx].innerHTML;
        const halfTextWidth = (text.length * CHAR_LENGTH) / 2;
        const xPosition = scale(timestamp) ?? 0;

        if (xPosition <= halfTextWidth / 2) {
          // shift the label to stick on left border
          return halfTextWidth / 2 - xPosition;
        }
        if (xPosition >= width - halfTextWidth) {
          // shift the label to stick on the right border
          return width - halfTextWidth - xPosition;
        }
        return 0;
      });
  });

  return (
    <g ref={groupRef}>
      <g>
        {label && (
          <text
            style={{
              fill: 'black',
              fontSize: LABEL_SIZE,
              transform: `translate(${x}px, ${y}px) rotate(${rotation}deg)`,
            }}
          >
            {label}
          </text>
        )}
      </g>
    </g>
  );
};

const getPosition = (
  position: Position,
  length: number,
  label: string | undefined,
  height: number | undefined,
) => {
  if (label == null) {
    return { x: 0, y: 0, rotation: 0 };
  }
  const labelOffset = (label.length * CHAR_LENGTH) / 2;

  switch (position) {
    case 'top':
      return { x: length / 2, y: -(height ?? 0), rotation: 0 };
    case 'bottom':
      return { x: length / 2, y: height, rotation: 0 };
    case 'left':
      return { x: -50, y: length / 2 - labelOffset, rotation: -90 };
    case 'right':
    default:
      return { x: 50, y: length / 2 - labelOffset, rotation: -90 };
  }
};

const getAxis = (position: Position) => {
  switch (position) {
    case 'top':
      return axisTop;
    case 'left':
      return axisLeft;
    case 'right':
      return axisRight;
    case 'bottom':
    default:
      return axisBottom;
  }
};
